import * as atoms from '@/config/globalStateControl';

import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { HoverCard, Text } from '@mantine/core';
import { useEffect, useState } from 'react';

import { API_ENDPOINT } from '@/config/endpoint';
import ConfirmationPopup from '@/components/ConfirmationPopup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconCheck } from '@tabler/icons-react';
import { faChartSimple } from '@fortawesome/free-solid-svg-icons';
import { mixpanelTrack } from '@/utils/mixpanel';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useVoteComponentStyle } from './discussion.style';

const VOTED_SIDE = {
  A: 'A',
  B: 'B',
};
const OPTION_1_LINEAR_COLOR = '109.6deg, rgba(51, 160, 254, 0.08), transparent';
const OPTION_2_LINEAR_COLOR = '109deg, #fafafa, transparent';

const VoteComponent = ({ discussionId, options, side, refetch }) => {
  const isSignedIn = useAtomValue(atoms.isSignedIn);

  const [optionA, setOptionA] = useState(options[0]);
  const [optionB, setOptionB] = useState(options[1]);

  const [voteButtonClicked, setVoteButtonClicked] = useState(false);

  const [timing, setTiming] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setTiming(true);
    }, 0);
  }, []);

  if (isSignedIn && side)
    return (
      <RenderResultComponent
        side={side}
        optionA={optionA}
        optionB={optionB}
        timing={timing}
      />
    );
  if (!isSignedIn || !voteButtonClicked)
    return (
      <RenderBlurComponent
        setVoteButtonClicked={setVoteButtonClicked}
        side={side}
        timing={timing}
      />
    );
  if (isSignedIn && !side)
    return (
      <RenderNormalComponent
        optionA={optionA}
        setOptionA={setOptionA}
        optionB={optionB}
        setOptionB={setOptionB}
        side={side}
        discussionId={discussionId}
        refetch={refetch}
        timing={timing}
      />
    );
  return null;
};

export default VoteComponent;

const RenderBlurComponent = ({ setVoteButtonClicked, side, timing }) => {
  const { classes } = useVoteComponentStyle();

  const isSignedIn = useAtomValue(atoms.isSignedIn);

  return (
    <div className={classes.blurComponentContainer}>
      <div className={classes.blurComponentBackground} />
      <div className={classes.blurComponentOptionContainer}>
        <Button
          type={ButtonTypes.whiteRaised}
          leftIcon={
            <FontAwesomeIcon
              icon={faChartSimple}
              size={'sm'}
              color={themeVariables.fontColors.light}
            />
          }
          style={{
            zIndex: 10,
            letterSpacing: '-0.03em',
            position: 'absolute',
            left: 'calc(50% - 100px)',
          }}
          onClick={() => {
            if (!isSignedIn) {
              ConfirmationPopup.login();
            } else {
              setVoteButtonClicked(true);
            }
          }}
        >
          {!isSignedIn ? '로그인 후 결과 확인하기' : '투표 후 결과 확인하기'}
        </Button>
        <RenderOption
          option={'로그인 필수'}
          percentage={50}
          color={OPTION_1_LINEAR_COLOR}
          isSelected={false}
          clickHandler={null}
          side={side}
          timing={timing}
        />
        <RenderOption
          option={'로그인 필수'}
          percentage={50}
          color={OPTION_2_LINEAR_COLOR}
          isSelected={false}
          clickHandler={null}
          side={side}
          timing={timing}
        />
      </div>
    </div>
  );
};

const RenderOption = ({
  option,
  percentage,
  color,
  isSelected,
  clickHandler,
  side,
  timing,
}) => {
  const { classes } = useVoteComponentStyle();

  return (
    <HoverCard
      withArrow
      arrowSize={10}
      width={'auto'}
      radius={'0.75rem'}
      position={'bottom'}
      shadow={themeVariables.boxShadow.popOverShadow}
    >
      <HoverCard.Target>
        <div
          style={{
            display: 'flex',
            backgroundImage: `linear-gradient(${color})`,
            width: side && timing ? `${percentage}%` : '50%',
            minWidth: 90,
            maxWidth: 'calc(100% - 90px)',
            height: 46,
            transition: side ? 'width 0.33s linear' : '',
          }}
          onClick={clickHandler}
        >
          <div className={classes.option}>
            <Text size={15} weight={600} color={themeVariables.fontColors.dark}>
              {option}
            </Text>
            <Text
              size={15}
              weight={600}
              mx={8}
              color={themeVariables.fontColors.dark}
            >
              {side ? `${percentage} %` : ''}
            </Text>
            {isSelected && <IconCheck size={14} stroke={3} />}
          </div>
        </div>
      </HoverCard.Target>
      {isSelected && (
        <HoverCard.Dropdown>
          <Text size={14}>내가 이전에 선택했던 옵션이에요</Text>
        </HoverCard.Dropdown>
      )}
    </HoverCard>
  );
};

const RenderNormalComponent = ({
  optionA,
  setOptionA,
  optionB,
  setOptionB,
  side,
  discussionId,
  refetch,
  timing,
}) => {
  const { classes } = useVoteComponentStyle();

  return (
    <>
      <div className={classes.normalComponentContainer}>
        <RenderOption
          option={`↑ ${VOTED_SIDE.A}`}
          percentage={50}
          color={OPTION_1_LINEAR_COLOR}
          isSelected={false}
          clickHandler={() => {
            ConfirmationPopup.confirm(
              `${VOTED_SIDE.A}에 투표하시겠습니까?`,
              () => {
                mixpanelTrack('DiscussionDetailPage_Option', 'Clicked');
                API_ENDPOINT.discussion
                  .voteOption(discussionId, optionA.id)
                  .then((res) => {
                    setOptionA(res.data.options[0]);
                    setOptionB(res.data.options[1]);
                    refetch();
                  });
              }
            );
          }}
          side={side}
          timing={timing}
        />
        <RenderOption
          option={`↓ ${VOTED_SIDE.B}`}
          percentage={50}
          color={OPTION_2_LINEAR_COLOR}
          isSelected={false}
          clickHandler={() => {
            ConfirmationPopup.confirm(
              `${VOTED_SIDE.B}에 투표하시겠습니까?`,
              () => {
                mixpanelTrack('DiscussionDetailPage_Option', 'Clicked');
                API_ENDPOINT.discussion
                  .voteOption(discussionId, optionB.id)
                  .then((res) => {
                    setOptionA(res.data.options[0]);
                    setOptionB(res.data.options[1]);
                    refetch();
                  });
              }
            );
          }}
          side={side}
          timing={timing}
        />
      </div>
    </>
  );
};

const RenderResultComponent = ({ side, optionA, optionB, timing }) => {
  const { classes } = useVoteComponentStyle();

  return (
    <div className={classes.resultComponentContainer}>
      <RenderOption
        option={'↑'}
        percentage={optionA.percent}
        color={OPTION_1_LINEAR_COLOR}
        isSelected={side === optionA.id}
        clickHandler={null}
        side={side}
        timing={timing}
      />
      <RenderOption
        option={'↑'}
        percentage={optionB.percent}
        color={OPTION_2_LINEAR_COLOR}
        isSelected={side === optionB.id}
        clickHandler={null}
        side={side}
        timing={timing}
      />
    </div>
  );
};
